// Docs: https://docs.netlify.com/functions/edge-functions/
import type { Config, Context } from '@netlify/edge-functions';
export const TOTAL_WEIGHT = 100;
export const CONTROL = 'control';
export const A_B_TEST_PATH_NAME = 'a-b-test-page';
export const A_B_TEST_COUNTRY_COOKIE_NAME = 'abt-cn';
export const A_B_TEST_COOKIE_HOST_PATH = '/';

export type ABTestConfig = {
  id: string;
  controlPath: string;
  startDate: string | null;
  endDate: string;
  variants: Variant[];
  personalizations: Personalization[];
  logicalOperator: 'and' | 'or';
};

export type Variant = {
  id: string;
  weight: number;
};

export type Personalization = {
  type: PersonalizationType;
  value: string;
};

type UTMKeys = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content';
export type PersonalizationType = 'country' | 'device' | UTMKeys;
export type FirstPartyDataList = Record<PersonalizationType, string>;

// NOTE The AB Test config compiled from the CMS is injected between the 2 comments below
// WHY? The Edge Function doesn't have access to the file system, so we can't read the config from an internal file
export const abTestConfig: ABTestConfig[] =
  // ab-test-config-start
  [
  {
    "id": "plans-and-pricing-sq-buy-now",
    "controlPath": "/plans-and-pricing/sonarqube/",
    "startDate": "2025-04-15T22:00:00Z",
    "endDate": "2025-07-31T05:00:00Z",
    "variants": [
      {
        "id": "plans-and-pricing-sq-buy-now",
        "weight": 50
      },
      {
        "id": "control",
        "weight": 50
      }
    ],
    "personalizations": [],
    "logicalOperator": "and"
  },
  {
    "id": "plans-and-pricing-sq-secondary-cta",
    "controlPath": "/plans-and-pricing/",
    "startDate": "2025-04-15T22:00:00Z",
    "endDate": "2025-07-31T05:00:00Z",
    "variants": [
      {
        "id": "plans-and-pricing-sq-secondary-cta",
        "weight": 50
      },
      {
        "id": "control",
        "weight": 50
      }
    ],
    "personalizations": [],
    "logicalOperator": "and"
  },
  {
    "id": "plans-and-pricing-sqc-secondary-cta",
    "controlPath": "/plans-and-pricing/sonarcloud/",
    "startDate": "2025-04-15T22:00:00Z",
    "endDate": "2025-07-31T05:00:00Z",
    "variants": [
      {
        "id": "plans-and-pricing-sqc-secondary-cta",
        "weight": 50
      },
      {
        "id": "control",
        "weight": 50
      }
    ],
    "personalizations": [],
    "logicalOperator": "and"
  },
  {
    "id": "sqc-free-promo",
    "controlPath": "/products/sonarcloud/signup-free/",
    "startDate": "2025-05-11T22:00:00Z",
    "endDate": "2025-07-31T05:00:00Z",
    "variants": [
      {
        "id": "sqc-free-promo",
        "weight": 50
      },
      {
        "id": "control",
        "weight": 50
      }
    ],
    "personalizations": [],
    "logicalOperator": "and"
  }
];
// ab-test-config-end

export default async (request: Request, { cookies, geo, next }: Context) => {
  const url = new URL(request.url);
  const pathname = url.pathname;
  const pageConfig = abTestConfig.find((abTest) => abTest?.controlPath === pathname);
  const isError = typeof pageConfig === 'undefined';

  if (isError) {
    console.log(
      'Error, this Edge Function is expecting an AB test config but is was not found, on: ',
      pathname
    );
    // Return default response
    return next();
  }
  const assignedVariantId = getAssignedVariantIdFromCookie({
    abTestConfig: pageConfig,
    cookies,
  });

  const firstPartyData = getFirstPartyData({
    abTestEndDate: pageConfig.endDate,
    cookies,
    request,
    geo,
  });

  const shouldExecute = shouldExecuteABTest({
    personalizations: pageConfig.personalizations,
    logicalOperator: pageConfig.logicalOperator,
    firstPartyData,
    isReturningVisitor: Boolean(assignedVariantId),
  });

  // The A/B Test has personalisation and criteria are not met
  if (!shouldExecute) {
    // Return default response
    return next();
  }

  const chosenVariantId =
    assignedVariantId ||
    selectNewVariantId({
      abTestConfig: pageConfig,
      cookies,
    });
  console.log(`A/B test "${pageConfig.id}" running with variant "${chosenVariantId}"`);

  // If no variant could be assigned or if the CONTROL variant has been assigned
  const shouldUseControlPage = !chosenVariantId || chosenVariantId === CONTROL;

  if (shouldUseControlPage) {
    // Return default response
    return next();
  }

  try {
    const metaRobotsRegexp = /<meta name="robots" content="(\\"|[^"])*"/;
    const currentResponse = await next();
    const currentBody = await currentResponse.text();

    const currentMetaRobots = metaRobotsRegexp.exec(currentBody)?.[0];

    console.log('Meta robots: ', currentMetaRobots);

    // If there is a page A/B test running and the assigned variant is not the control variant
    const variantPath = `/${A_B_TEST_PATH_NAME}/${pageConfig.id}/${chosenVariantId}/`;

    // Check if the server has Basic Auth credentials and pass them the variant request (username:password@url)
    const AUTH_VALUE = Netlify.env.get('AUTH_VALUE');
    const requestUrl = AUTH_VALUE
      ? request.url.replace('https://', `https://${AUTH_VALUE}@`)
      : request.url;

    const variantUrl = new URL(variantPath, requestUrl);
    const variantResponse = await fetch(variantUrl);
    console.log('Fetched variant page from: ', variantPath);

    // Replace the pagePath variable in the HTML with the new path
    // This avoids Gatsby from changing the URL in the browser to the variants URL
    let variantBody = (await variantResponse.text()).replace(
      `window.pagePath="${variantPath}`,
      `window.pagePath="${pathname}`
    );

    if (currentMetaRobots) {
      console.log('Replacing meta robots');
      variantBody = variantBody.replace(metaRobotsRegexp, currentMetaRobots);
    }

    return new Response(variantBody, variantResponse);
  } catch (error) {
    console.log('Error processing variant page: ', error);
    // Reset cookie to CONTROL to avoid content flashing, since we forcefully return the default (control) response
    cookies.set({
      name: pageConfig.id,
      value: CONTROL,
      path: A_B_TEST_COOKIE_HOST_PATH,
      expires: new Date(pageConfig.endDate),
    });
    // Return default response
    return next();
  }
};

export const config: Config = {
  path: abTestConfig.length
    ? (abTestConfig.map((test) => test.controlPath) as `/${string}`[])
    : undefined,
  onError: 'bypass',
};

/*
 * Utils shared with Gatsby
 */
export const getRandomVariant = (variants: Variant[]): Variant | null => {
  // Get a random number between 1 and 1000
  const userScore = Math.floor(Math.random() * TOTAL_WEIGHT) + 1;

  let maxWeight = 0;
  let minWeight = 0;

  for (const variant of variants) {
    maxWeight += variant.weight;

    // Loop sequence examples with 3 variants having weights 10, 20 and 40, where X is the userScore:
    // 0 < X <= 10, 10 < X <= 30, 30 < X <= 70
    if (userScore > minWeight && userScore <= maxWeight) {
      return variant;
    }

    minWeight = maxWeight;
  }

  console.log(`getRandomVariant: Could not match any variant in the A/B test. This is a bug.`);
  return null;
};

export const shouldExecuteABTest = ({
  personalizations,
  logicalOperator,
  firstPartyData,
  isReturningVisitor,
}: {
  personalizations: Personalization[];
  logicalOperator: ABTestConfig['logicalOperator'];
  firstPartyData: Record<PersonalizationType, string>;
  isReturningVisitor: boolean;
}): boolean => {
  // NOTE If no personalizations are set or if the user has already been assigned a variant, do not abort
  if (personalizations.length === 0 || isReturningVisitor) {
    return true;
  }

  if (logicalOperator === 'and') {
    // NOTE Abort if not all criteria are met
    return personalizations.every((personalization) => {
      const { type, value } = personalization;
      return firstPartyData[type] === value;
    });
  }

  // NOTE logicalOperator === 'or'
  return personalizations.some((personalization) => {
    const { type, value } = personalization;
    return firstPartyData[type] === value;
  });
};

export const getUtmParams = (url: URL) => {
  const searchParams = url.searchParams;
  const utmParams: Record<UTMKeys, string> = {
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
  };

  searchParams.forEach((value, key) => {
    if (key in utmParams) {
      utmParams[key as UTMKeys] = value;
    }
  });

  return utmParams;
};

export const isUserAgentMobile = (userAgent: string) => {
  // SOURCE: https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
  return userAgent.includes('Mobi');
};

/*
 * Utils
 *
 * They are not exported because they are specific to the Edge Function. Gatsby can't use them.
 * Gatsby uses its own version of these utils, see in: @/utils/a-b-test/
 */

/*
 * NOTE A counterpart of this function for client-side exists in: @/utils/a-b-test/getABTest.ts
 */
const selectNewVariantId = ({
  abTestConfig,
  cookies,
}: {
  abTestConfig: ABTestConfig;
  cookies: Context['cookies'];
}): string | null => {
  const abTestId = abTestConfig.id;
  const variants = abTestConfig.variants;
  const newVariant = getRandomVariant(variants);

  if (!newVariant) {
    console.log(
      `selectNewVariantId: There was an error assigning a variant for the A/B Test ${abTestId}.`
    );
    return null;
  }

  // NOTE Save the assigned variant in a cookie for client-side to access it
  cookies.set({
    name: abTestId,
    value: newVariant.id,
    path: A_B_TEST_COOKIE_HOST_PATH,
    expires: new Date(abTestConfig.endDate),
  });

  return newVariant.id;
};

/*
 * NOTE A counterpart of this function for client-side exists in: @/utils/a-b-test/getABTest.ts
 */
const getAssignedVariantIdFromCookie = ({
  abTestConfig,
  cookies,
}: {
  abTestConfig: ABTestConfig;
  cookies: Context['cookies'];
}): string | undefined => {
  const activeVariantId = cookies.get(abTestConfig.id);
  const variants = abTestConfig.variants;
  return variants.find((variant) => activeVariantId === variant.id)?.id;
};

/*
 * NOTE A counterpart of this function for client-side exists in: @/utils/a-b-test/getFirstPartyData.ts
 */
const getFirstPartyData = ({
  abTestEndDate,
  cookies,
  request,
  geo,
}: {
  abTestEndDate: string;
  cookies: Context['cookies'];
  request: Request;
  geo: Context['geo'];
}): FirstPartyDataList => {
  const url = new URL(request.url);
  const countryCode = geo.country?.code || '';
  const isMobile = isUserAgentMobile(request.headers.get('User-Agent') ?? '');

  // NOTE Save the country code in a cookie, so that client-side can access it
  cookies.set({
    name: A_B_TEST_COUNTRY_COOKIE_NAME,
    value: countryCode,
    path: A_B_TEST_COOKIE_HOST_PATH,
    expires: new Date(abTestEndDate),
  });

  return {
    country: countryCode,
    device: isMobile ? 'mobile' : 'desktop',
    ...getUtmParams(url),
  };
};
